import React, { useState, useEffect } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import apiClient from '../../services/apiService';
import Skeleton from '../common/Skeleton';
import Badge from '../common/Badge';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import NoDataFound from '../common/NoDataFound';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { statusVariant } from '../../utils/statusVariant';

interface BacklogItem {
    id: string;
    kind: 'filing' | 'litigation';
    name: string;
    sub: string;
    type: string;
    dueDate: string;
    daysOverdue: number;
    stepInfo: string;
    stepsDone?: number;
    stepsTotal?: number;
    owner: string;
    status: string;
}

function daysOverdue(dateStr: string): number {
    const due = new Date(dateStr);
    due.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.floor((now.getTime() - due.getTime()) / 86_400_000);
}

const ComplianceBacklog: React.FC = () => {
    const { allowed: canView, loading: permLoading } = usePermission('COM_COMPLIANCE_REGISTER', 'view');
    const { formatDate } = useEntitySettings();
    const [items, setItems] = useState<BacklogItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        Promise.all([
            apiClient.get('/api/v1/com/stat-instances?view=all'),
            apiClient.get('/api/v1/com/litigations'),
        ])
            .then(([instRes, litRes]) => {
                const instances: any[] = instRes.data.data || [];
                const lits: any[] = litRes.data.data || [];

                const filingItems: BacklogItem[] = instances
                    .filter(i => {
                        if (!i.due_date) return false;
                        if (i.status === 'COMPLETED' || i.status === 'CANCELLED') return false;
                        return new Date(i.due_date) < today;
                    })
                    .map(i => ({
                        id: `f-${i.id}`,
                        kind: 'filing' as const,
                        name: i.filing_name || `Filing #${i.id}`,
                        sub: [i.period_label, i.location_name].filter(Boolean).join(' · '),
                        // frequency dropped from the obligation definition — cadence owns scheduling.
                        type: i.obligation_type || 'Filing',
                        dueDate: i.due_date,
                        daysOverdue: daysOverdue(i.due_date),
                        stepInfo: `${i.completed_count}/${i.step_count} steps`,
                        stepsDone: i.completed_count ?? 0,
                        stepsTotal: i.step_count ?? 0,
                        owner: i.initiated_by_name || '—',
                        status: i.status.replace('_', ' '),
                    }));

                const litItems: BacklogItem[] = lits
                    .filter(l => {
                        if (!l.next_due_date) return false;
                        if ((l.status || '').toLowerCase() === 'closed') return false;
                        return new Date(l.next_due_date) < today;
                    })
                    .map(l => ({
                        id: `l-${l.id}`,
                        kind: 'litigation' as const,
                        name: l.notice_id || `Notice #${l.id}`,
                        sub: l.law_name || '',
                        type: 'Litigation',
                        dueDate: l.next_due_date,
                        daysOverdue: daysOverdue(l.next_due_date),
                        stepInfo: l.current_step_label || '—',
                        owner: l.assigned_user_name || '—',
                        status: l.status || 'Open',
                    }));

                const all = [...filingItems, ...litItems].sort((a, b) => b.daysOverdue - a.daysOverdue);
                setItems(all);
            })
            .catch(() => setError('Failed to load backlog.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { setPage(1); }, [search, pageSize]);

    const filtered = items.filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase()) ||
        i.owner.toLowerCase().includes(search.toLowerCase())
    );
    const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

    // ── Not-authorised gate ─────────────────────────────────────────────────
    if (!permLoading && !canView) {
        return (
            <NoDataFound title="Not authorised" message="You do not have access to 'COM_BACKLOG'." />
        );
    }

    return (
        <div className="netbody-card flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_compliance_backlog_search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Obligation / Notice</th>
                                        <th className="table-list-th">Type</th>
                                        <th className="table-list-th">Due Date</th>
                                        <th className="table-list-th">Days Overdue</th>
                                        <th className="table-list-th" style={{ width: 110 }}>Progress</th>
                                        <th className="table-list-th">Responsible</th>
                                        <th className="table-list-th table-list-cell--status">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'xs', 'md', 'sm', 'xs', 'xs', 'xs', 'pill']} />}
                                    {!loading && error && (
                                        <tr>
                                            <td colSpan={8} className="table-list-cell text-red-500 text-sm text-center p-4">{error}</td>
                                        </tr>
                                    )}
                                    {!loading && !error && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="table-list-cell">
                                                <NoDataFound
                                                    title="No Backlog Items"
                                                    message={search ? 'No items match your search.' : 'All compliance items are up to date.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && !error && paged.map((item, idx) => (
                                        <tr
                                            key={item.id}
                                            className="table-list-row"
                                            style={item.daysOverdue > 7 ? { background: '#fef2f2' } : { background: '#fffbeb' }}
                                        >
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">{item.name}</span>
                                                {item.sub && <span className="table-list-cell-sub">{item.sub}</span>}
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="status-badge sm neutral">{item.type}</span>
                                            </td>
                                            <td className="table-list-cell font-medium text-red-600 text-sm">{formatDate(item.dueDate)}</td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-red-600 font-semibold text-sm">
                                                    <AlertTriangle size={12} />
                                                    {item.daysOverdue}d
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                {item.stepsTotal != null ? (
                                                    <div className="step-progress-bar" style={{ minWidth: 0, maxWidth: 100 }}>
                                                        <div className="step-progress-bar__track">
                                                            <div
                                                                className={`step-progress-bar__fill ${item.stepsTotal > 0 && (item.stepsDone ?? 0) >= item.stepsTotal ? 'bg-green-400' : 'bg-amber-400'}`}
                                                                style={{ width: `${Math.round(((item.stepsDone ?? 0) / Math.max(item.stepsTotal, 1)) * 100)}%` }}
                                                            />
                                                        </div>
                                                        <span className="step-progress-bar__text">{item.stepsDone}/{item.stepsTotal}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-600">{item.stepInfo}</span>
                                                )}
                                            </td>
                                            <td className="table-list-cell text-sm">{item.owner}</td>
                                            <td className="table-list-cell">
                                                <Badge variant={statusVariant(item.status)} size="sm">{item.status}</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={filtered.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={[10, 25, 50]}
                            recordLabel="items"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComplianceBacklog;
